import { useRef } from "react";
import { animate, motion, useMotionValue, useTransform } from "framer-motion";
import { Bookmark, ThumbsDown, ThumbsUp } from "lucide-react";
import { posterUrl } from "@/lib/api";

export const SEEN_ACTIONS = {
  liked: { side: "left", direction: "down", label: "Didn't like", toast: "Moved to Didn't like", icon: ThumbsDown, color: "--sp-down" },
  disliked: { side: "right", direction: "up", label: "Liked", toast: "Moved to Liked", icon: ThumbsUp, color: "--sp-up" },
  passed: { side: "right", direction: "right", label: "Watchlist", toast: "Added to Watchlist", icon: Bookmark, color: "--sp-right" },
};
const THRESHOLD = 90;

export function SeenRow({ movie, kind, rank, onOpen, onMove }) {
  const action = SEEN_ACTIONS[kind];
  const sign = action.side === "left" ? -1 : 1;
  const x = useMotionValue(0);
  const reveal = useTransform(x, sign < 0 ? [-THRESHOLD, -20] : [20, THRESHOLD], sign < 0 ? [1, 0] : [0, 1]);
  const dragged = useRef(false);
  const Icon = action.icon;

  const commit = () => {
    animate(x, sign * 600, { duration: 0.22 }).then(() => onMove(movie, action));
  };
  const onDragEnd = (_, info) => {
    if (sign * info.offset.x > THRESHOLD || sign * info.velocity.x > 700) commit();
    else animate(x, 0, { type: "spring", stiffness: 400, damping: 32 });
    setTimeout(() => { dragged.current = false; }, 0);
  };

  return (
    <li className="relative rounded-2xl overflow-hidden" data-testid={`seen-item-${movie.movie_id}`}>
      <motion.div style={{ opacity: reveal, background: `hsl(var(${action.color}))` }}
        className={`absolute inset-0 flex items-center px-6 text-white font-bold gap-2 ${sign < 0 ? "justify-end" : "justify-start"}`}>
        <Icon className="h-5 w-5" /> {action.label}
      </motion.div>
      <motion.div drag="x" dragDirectionLock dragConstraints={sign < 0 ? { left: -400, right: 0 } : { left: 0, right: 400 }}
        dragElastic={{ left: sign < 0 ? 0.6 : 0, right: sign > 0 ? 0.6 : 0 }} style={{ x, touchAction: "pan-y" }}
        onDragStart={() => { dragged.current = true; }} onDragEnd={onDragEnd}
        onClick={() => { if (!dragged.current) onOpen(movie); }}
        className="relative flex items-center gap-4 p-3 rounded-2xl border bg-card cursor-pointer select-none transition-colors hover:border-foreground/25">
        {rank && <span className="w-8 text-center font-display font-black text-xl text-muted-foreground">{rank}</span>}
        <img src={posterUrl(movie.poster_path, "w154")} alt={movie.title} loading="lazy" draggable={false} className="h-20 w-14 rounded-lg object-cover" />
        <div className="min-w-0 flex-1">
          <p className="font-display font-bold text-lg leading-tight truncate">{movie.title}</p>
          <p className="text-xs text-muted-foreground">{movie.year} · {movie.genres.slice(0, 2).join(", ") || "—"}</p>
        </div>
        <span className="text-xs text-muted-foreground hidden md:block">{new Date(movie.seen_at).toLocaleDateString()}</span>
        <button onClick={(e) => { e.stopPropagation(); commit(); }} aria-label={`Move to ${action.label}`}
          data-testid={`seen-move-${movie.movie_id}`} title={`Move to ${action.label}`}
          className="shrink-0 h-9 w-9 rounded-full border flex items-center justify-center text-muted-foreground transition-colors hover:text-white"
          onMouseEnter={(e) => { e.currentTarget.style.background = `hsl(var(${action.color}))`; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}>
          <Icon className="h-4 w-4" />
        </button>
      </motion.div>
    </li>
  );
}
